/**
 * 曲线对话框（Ctrl+M，参照 Photoshop「图像→调整→曲线」，破坏性）。
 * SVG 里拖点（跟 AdjustmentPanel.tsx 的 CurvesEditor 同一个手感，但那个
 * 耦合在 AdjustmentLayer 上不能直接复用，这里是独立状态的简化版），
 * 实时预览，确定后一次性改活动图层像素。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { CurvePoint } from '../../engine/adjustments/lut';
import styles from './CurvesDialog.module.css';

const CURVE_SIZE = 255;
const DEFAULT_POINTS: readonly [number, number][] = [
  [0, 0],
  [128, 128],
  [255, 255],
];

export interface CurvesDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onApply: (points: readonly CurvePoint[]) => void;
  readonly previewUrl?: string | undefined;
  readonly onPreview?: ((points: readonly CurvePoint[]) => void) | undefined;
}

export function CurvesDialog(props: CurvesDialogProps): JSX.Element {
  const [points, setPoints] = createSignal<readonly [number, number][]>(DEFAULT_POINTS);
  let svg: SVGSVGElement | undefined;
  let dragIndex = -1;
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) setPoints(DEFAULT_POINTS);
  });

  const asCurvePoints = (): readonly CurvePoint[] => points().map(([x, y]) => ({ x, y }));

  createEffect(() => {
    if (!props.open) return;
    props.onPreview?.(asCurvePoints());
  });

  const eventPoint = (event: PointerEvent): { x: number; y: number } => {
    const rect = svg!.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * CURVE_SIZE;
    const y = CURVE_SIZE - ((event.clientY - rect.top) / rect.height) * CURVE_SIZE;
    return {
      x: Math.min(CURVE_SIZE, Math.max(0, Math.round(x))),
      y: Math.min(CURVE_SIZE, Math.max(0, Math.round(y))),
    };
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (dragIndex < 0) return;
    const pts = [...points()];
    const { x, y } = eventPoint(event);
    const isFirst = dragIndex === 0;
    const isLast = dragIndex === pts.length - 1;
    const minX = isFirst ? 0 : pts[dragIndex - 1]![0] + 1;
    const maxX = isLast ? CURVE_SIZE : pts[dragIndex + 1]![0] - 1;
    const nextX = isFirst ? 0 : isLast ? CURVE_SIZE : Math.min(maxX, Math.max(minX, x));
    pts[dragIndex] = [nextX, y];
    setPoints(pts);
  };

  const onPointerUp = (): void => {
    dragIndex = -1;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  };

  const beginDrag = (index: number): void => {
    dragIndex = index;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const polyline = (): string =>
    points()
      .map(([x, y]) => `${x},${CURVE_SIZE - y}`)
      .join(' ');

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="curves-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="曲线"
          data-testid="curves-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>曲线</h2>

          <Show when={props.previewUrl !== undefined}>
            <div class={styles.previewPanel}>
              <img class={styles.previewImage} src={props.previewUrl} alt="曲线预览" data-testid="curves-preview" />
            </div>
          </Show>

          <svg
            ref={(el) => {
              svg = el;
            }}
            class={styles.curves}
            viewBox={`0 0 ${CURVE_SIZE} ${CURVE_SIZE}`}
            data-testid="curves-graph"
          >
            <line class={styles.curveGuide} x1="0" y1={CURVE_SIZE} x2={CURVE_SIZE} y2="0" />
            <polyline class={styles.curveLine} points={polyline()} />
            <For each={points()}>
              {([x, y], index) => (
                <circle
                  class={styles.curvePoint}
                  cx={x}
                  cy={CURVE_SIZE - y}
                  r="8"
                  data-testid={`curves-point-${index()}`}
                  on:pointerdown={(event) => {
                    event.preventDefault();
                    beginDrag(index());
                  }}
                />
              )}
            </For>
          </svg>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="curves-confirm"
              onClick={() => props.onApply(asCurvePoints())}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
